import { PlanCard } from './PlanCard';
import { BillingToggle, SubscribeButton } from './PricingControls';
import { dsColors } from '../../design-system/tokens/colors';
import { Icon } from '../../design-system/icons';
import { BENEFICIOS_DE_TODOS_LOS_PLANES, type PricingPlanKey } from './pricingContent';
import { PRICING_COPY_SUSCRIPCION } from './pricingContexts';
import { badgeDelPlan, LinkPlanesDeEquipo, type PricingCatalogProps } from './PricingCatalog';
import { BannerDePromo, promoDeLosPlanes } from './BannerDePromo';

/**
 * Catálogo **comparativo**: los tres planes uno al lado del otro — ADR-2026-07-31-embebido-deja-ser-modal-scrim.
 *
 * Es la presentación de `variant="embedded"`, la que usa Configuración → Plan. Se
 * separa del modal de dos paneles (`PricingDesktop`) porque resuelven problemas
 * distintos:
 *
 * - El **modal** interrumpe: aparece sobre lo que el usuario estaba haciendo, tiene
 *   que caber en el viewport de una y por eso muestra **un** plan por vez, con un
 *   segmentado para cambiarlo. El carrusel de la izquierda está para argumentar
 *   frente a alguien que no vino a comprar.
 * - **Acá el usuario entró a mirar planes.** Tiene toda la pantalla, no hay nada
 *   que interrumpir y lo que necesita es **comparar**: los tres a la vez, sin
 *   clicks intermedios. Por eso se van el carrusel (ya está adentro, no hay que
 *   convencerlo de entrar) y el segmentado de planes (no queda un plan que elegir
 *   para ver: están los tres).
 *
 * Sin cruz de cerrar: esta presentación no tapa nada, así que no hay nada que
 * cerrar. La vuelta la pone la pantalla que lo monta.
 *
 * Reusa las MISMAS piezas que el modal —`PlanCard`, `BillingToggle`,
 * `SubscribeButton`— y los mismos `plans` de `buildPlanViews`: precios, cupón y
 * etiqueta de CTA salen de un solo lugar para las dos presentaciones.
 */
export function PricingComparativa<K extends string = PricingPlanKey>({
  plans,
  onSubscribe,
  processing,
  ultraTier,
  showBillingToggle,
  annual,
  onAnnualChange,
  note,
  error,
  contextCopy,
  beneficios: beneficiosPropios,
  beneficiosTitulo,
  onVerPlanesDeEquipo,
  textoLinkAlPie,
}: PricingCatalogProps<K>) {
  // Con el switch en "Anual" las cards muestran las cifras anuales, igual que en el
  // modal. Siguen siendo cifras derivadas del mensual (ADR-2026-07-28-switch-mensual-anual-visual-solo), no un precio real.
  const verAnual = showBillingToggle && annual;
  const copy = contextCopy ?? PRICING_COPY_SUSCRIPCION;
  // La factura A o B en pesos es sólo para Argentina: el dato llega en los planes
  // (`ivaIncluido`, que `buildPlanViews` arma con el país de la cuenta).
  const esArgentina = plans.some((plan) => plan.ivaIncluido);
  const beneficios =
    beneficiosPropios ??
    BENEFICIOS_DE_TODOS_LOS_PLANES.filter((b) => !b.soloArgentina || esArgentina).map((b) => b.texto);

  return (
    <div className="pricing-cursor-scope w-full">
      {/* El encabezado va SIEMPRE: en el modal esa función la cumplía el carrusel
          de la izquierda, y acá sin él la pantalla arrancaba en el switch de
          período, sin decir de qué se trata.

          `/subscription` no pasa `contextCopy` —se llega a propósito, no desde un
          momento que lo dispare—, así que cae a `PRICING_COPY_SUSCRIPCION`
          (ADR-2026-07-31-copy-propio-default-comparativo). El default se resuelve acá y no en `Paywall` para no tocar
          mobile: allá `contextCopy` **reemplaza** el copy por imagen del carrusel,
          y en esta presentación no hay carrusel que reemplazar. */}
      <header className="mx-auto mb-7 max-w-[620px] text-center">
        <h2
          className="text-[26px] font-[700] leading-[32px]"
          style={{ color: dsColors.neutral[900] }}
        >
          {copy.title}
        </h2>
        <p
          className="mt-2 text-[14.5px] font-[500] leading-[21px]"
          style={{ color: dsColors.neutral[600] }}
        >
          {copy.description}
        </p>
      </header>

      {/* Promo de temporada (Cyber Monday…), entre el título y los planes. A todo el
          ancho: con las fotos a los costados el texto ocupa el 56% del medio. */}
      {!verAnual ? (
        <BannerDePromo
          {...promoDeLosPlanes(plans)}
          forma="fila"
          fotos
          className="mb-9"
        />
      ) : null}

      {showBillingToggle && (
        <div className="mb-6 flex justify-center">
          <BillingToggle annual={annual} onChange={onAnnualChange} />
        </div>
      )}

      {/* Lo que traen los tres planes, una sola vez arriba en vez de repetido en
          cada card (mismo bloque que la landing de precios). */}
      {beneficiosTitulo ? (
        <p
          className="mb-2 text-center text-[13px] font-[600] leading-[18px]"
          style={{ color: dsColors.neutral[900] }}
        >
          {beneficiosTitulo}
        </p>
      ) : null}
      <ul className="mb-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
        {beneficios.map((b) => (
          <li key={b} className="flex items-center gap-1.5">
            <Icon
              name="outline/check"
              size={18}
              className="shrink-0"
              style={{ color: 'var(--brand-primary)' }}
            />
            <span
              className="text-[14px] font-[500] leading-[19px]"
              style={{ color: dsColors.neutral[600] }}
            >
              {b}
            </span>
          </li>
        ))}
      </ul>

      {/* Una columna por plan en desktop; apiladas por debajo de `lg`, donde tres
          cards no entran sin comprimir el precio hasta volverlo ilegible.

          Las tres cards se estiran al alto de la más larga (el `stretch` por
          defecto del grid) y el CTA va con `mt-auto`: así los tres botones quedan
          en la misma línea. Con cada card midiendo por su contenido, los CTAs
          quedaban a tres alturas distintas y la comparación se leía peor. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {plans.map((plan) => (
          // `relative` para el badge "Popular", que sobresale por encima del borde
          // de la card. No va en el plan vigente: ahí manda "Tu plan".
          <div key={plan.key} className="relative h-full">
            {badgeDelPlan(plan) && !plan.isCurrent ? (
              <span
                className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-[700] uppercase leading-[15px] tracking-wide text-white"
                style={{ background: 'var(--brand-primary)' }}
              >
                {badgeDelPlan(plan)}
              </span>
            ) : null}
            <PlanCard
              content={plan.content}
              credits={plan.credits}
              creditsText={plan.creditsText}
              creditsDetail={plan.creditsDetail}
              generates={plan.generates}
              price={verAnual ? plan.annual.perMonth : plan.price}
              period={verAnual ? 'por mes' : plan.period}
              priceNote={verAnual ? `${plan.annual.total} por año` : plan.priceNote}
              strikePrice={verAnual ? undefined : plan.strikePrice}
              discountLabel={verAnual ? undefined : plan.discountLabel}
              ivaIncluido={plan.ivaIncluido}
              // El slider de tiers es exclusivo de Ultra: es lo que define cuántos
              // créditos trae, y los otros dos no tienen niveles.
              ultraTier={plan.tierSlider ?? (plan.key === 'enterprise' ? ultraTier : undefined)}
              isCurrent={plan.isCurrent}
              className="h-full"
              footer={
                <div className="mt-auto pt-2">
                  <SubscribeButton
                    label={plan.ctaLabel}
                    onClick={() => onSubscribe(plan.key)}
                    disabled={plan.ctaDisabled}
                    loading={processing}
                  />
                  {/* "Cancelar suscripción" sólo aparece en la card del plan
                    vigente, que es de donde se cancela. */}
                  {plan.secondaryLabel && plan.onSecondary && (
                    <button
                      type="button"
                      onClick={plan.onSecondary}
                      className="mt-2 w-full text-center text-[13px] font-[600] leading-[17px] text-neutral-500 underline underline-offset-2"
                    >
                      {plan.secondaryLabel}
                    </button>
                  )}
                </div>
              }
            />
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-4 text-center text-[13px] font-medium text-red-600">
          {error}
        </p>
      )}

      {note && (
        <div className="mt-6 text-center text-[13px] leading-[18px] text-neutral-500 [&_p]:mx-auto">
          {note}
        </div>
      )}
      {onVerPlanesDeEquipo ? (
        <div className="mt-4 text-center">
          <LinkPlanesDeEquipo onClick={onVerPlanesDeEquipo} texto={textoLinkAlPie} />
        </div>
      ) : null}
    </div>
  );
}

export default PricingComparativa;
